/**
 * 组织方聚合看板面板（扩展⑤）：实时 SQL 聚合，只出计数不出明细。
 * - 任何由人派生的桶 n<5 一律 "<5"（k-匿名），后端已做披露控制，前端原样展示；
 * - 解读文字的数字由后端代码模板拼死（LLM 仅润色且必须保留全部数字），via 如实标注。
 */
import { useQuery } from '@tanstack/react-query';

import { eventsApi } from '../api/events.js';
import { ErrorState } from './ErrorState.js';
import { Loading } from './Loading.js';
import { Tag } from './Tag.js';
import { formatDateTime } from '../lib/format.js';

export function EventStatsPanel({ eventCode }: { eventCode: string }) {
  const statsQuery = useQuery({
    queryKey: ['event-stats', eventCode],
    queryFn: () => eventsApi.stats(eventCode),
  });

  if (statsQuery.isPending) {
    return <Loading label="正在计算聚合看板…" rows={3} />;
  }
  if (statsQuery.isError || statsQuery.data === undefined) {
    return (
      <ErrorState error={statsQuery.error} title="读取聚合看板失败" onRetry={() => void statsQuery.refetch()} />
    );
  }

  const stats = statsQuery.data;
  const visibleSchools = stats.schoolDist.filter((item) => typeof item.count === 'number');
  const hiddenSchools = stats.schoolDist.length - visibleSchools.length;
  const maxSchool = visibleSchools.reduce((max, item) => Math.max(max, typeof item.count === 'number' ? item.count : 0), 0);

  return (
    <div className="stack">
      <div className="row-tight">
        <span className="card-sub">
          实时聚合 · 生成于 {formatDateTime(stats.generatedAt)} · 只出计数不出明细
        </span>
        <button type="button" className="btn btn-ghost btn-sm right" onClick={() => void statsQuery.refetch()}>
          刷新
        </button>
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <span className="stat-value">{stats.teams.total}</span>
          <span className="stat-label">队伍总数（已成队 {stats.teams.confirmed} · 组建中 {stats.teams.forming}）</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.groupedPeople}</span>
          <span className="stat-label">已组队人数（跨队去重）</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{stats.avgFormingHours === null ? '—' : `${stats.avgFormingHours} h`}</span>
          <span className="stat-label">平均成队耗时</span>
        </div>
      </div>

      <div className="grid-2">
        <section className="stack-sm" aria-label="各校分布">
          <span className="card-sub">各校已组队人数（n&lt;5 按隐私保护只显示 &quot;&lt;5&quot;）</span>
          {stats.schoolDist.length === 0 ? (
            <p className="muted small">还没有已组队的学生。</p>
          ) : (
            stats.schoolDist.map((item) => (
              <div key={item.school} className="stat-bar-row">
                <span className="stat-bar-label">{item.label}</span>
                <div className="stat-bar-track">
                  {typeof item.count === 'number' && maxSchool > 0 ? (
                    <div className="stat-bar-fill" style={{ width: `${Math.round((item.count / maxSchool) * 100)}%` }} />
                  ) : null}
                </div>
                <span className="stat-bar-value">{item.count}</span>
              </div>
            ))
          )}
          {hiddenSchools > 0 ? (
            <p className="field-hint">{hiddenSchools} 个学校不足 5 人，已按 k-匿名隐藏。</p>
          ) : null}
        </section>

        <section className="stack-sm" aria-label="缺口榜">
          <span className="card-sub">能力缺口榜（缺该技能的队伍数）</span>
          {stats.skillGaps.length === 0 ? (
            <p className="muted small">赛事尚未维护技能需求，或当前队伍没有缺口。</p>
          ) : (
            stats.skillGaps.map((item) => (
              <div key={item.skill} className="row-tight">
                <Tag tone="warn">{item.label}</Tag>
                <span className="muted small">{item.teamsMissing} 支队缺</span>
              </div>
            ))
          )}
        </section>
      </div>

      <div className="feedback-suggestion">
        <div className="row-tight">
          <Tag tone={stats.interpretation.via === 'llm' ? 'primary' : 'muted'}>
            数据解读 · via {stats.interpretation.via === 'llm' ? 'LLM 润色' : '规则模板'}
          </Tag>
          <span className="muted small">数字由代码模板拼死，LLM 仅润色</span>
        </div>
        <p className="wrap-anywhere">{stats.interpretation.text}</p>
      </div>
    </div>
  );
}
